import type { Cents } from "../domain/money";
import type { FinancialBucket } from "../domain/buckets";
import type { ActualExpense } from "../domain/actualExpense";
import type { PurchaseGraph } from "../domain/purchase";
import type { MileageEntry } from "../domain/mileage";
import type { CostType } from "../domain/proposedCostLine";
import { computePurchase } from "./purchaseMath";

/**
 * Normalized Actual-Cost Event layer (Phase 5).
 *
 * The financial engine reasons about ONE representation of "money that
 * actually hit a project", produced from three peer sources:
 *   - Manual Actual Expenses     (permanent, single-project)
 *   - Purchase Allocations       (project slice of a multi-project purchase;
 *                                  returns are negative events)
 *   - Mileage entries            (miles × frozen rate)
 *
 * Only allocations — never whole purchases — become events, so a
 * multi-project receipt is counted once per project and never in full.
 * Each event carries at most one direct scope link, so line/feature
 * actuals count every dollar exactly once. This is an internal engine
 * abstraction, not a table.
 */
export type ActualCostSource = "expense" | "allocation" | "mileage";

export interface ActualCostEvent {
  id: string;
  source: ActualCostSource;
  projectId: string;
  /** Pre-tax amount (includes any allocated shipping). */
  amountCents: Cents;
  taxCents: Cents;
  categoryId: string;
  /**
   * Cost BASIS (Phase 9C): what kind of money this is, so reconciliation
   * compares like with like. NULL means the source did not state one (a
   * lump-sum expense covering mixed scope) — such money is reported as
   * "not comparable", never guessed into material or labor.
   */
  costType: CostType | null;
  bucketOverride: FinancialBucket | null;
  linkedProposedLineId: string | null;
  linkedFeatureId: string | null;
  /** Non-null events are excluded from all totals. */
  voidedAt: string | null;
  date: string;
  description: string;
}

export const MILEAGE_CATEGORY_ID = "mileage";

export function expenseEvents(expenses: ActualExpense[]): ActualCostEvent[] {
  return expenses.map((e) => ({
    id: e.id,
    source: "expense",
    projectId: e.projectId,
    amountCents: e.amountCents,
    taxCents: e.taxCents,
    categoryId: e.categoryId,
    costType: e.costType,
    bucketOverride: e.bucketOverride,
    linkedProposedLineId: e.linkedProposedLineId,
    linkedFeatureId: e.linkedFeatureId,
    voidedAt: e.voidedAt,
    date: e.date,
    description: e.description,
  }));
}

/** Allocation events across the given purchase graphs (all projects). */
export function allocationEvents(graphs: PurchaseGraph[]): ActualCostEvent[] {
  const events: ActualCostEvent[] = [];
  for (const graph of graphs) {
    const calc = computePurchase(graph);
    for (const alloc of graph.allocations) {
      const c = calc.allocations.find((x) => x.allocationId === alloc.id);
      const line = graph.lines.find((l) => l.id === alloc.lineId);
      if (!c || !line) continue;
      events.push({
        id: alloc.id,
        source: "allocation",
        projectId: alloc.projectId,
        amountCents: c.amountCents + c.shippingCents,
        taxCents: c.taxCents,
        categoryId: alloc.categoryOverrideId ?? line.categoryId ?? "miscellaneous",
        // The purchase LINE states the basis (a receipt line is materials
        // or labor); the allocation only splits it between projects.
        costType: line.costType,
        bucketOverride: alloc.bucketOverride,
        linkedProposedLineId: alloc.linkedProposedLineId,
        linkedFeatureId: alloc.linkedFeatureId,
        voidedAt: graph.purchase.voidedAt,
        date: graph.purchase.date,
        description: `${graph.purchase.vendor}: ${line.description}`,
      });
    }
  }
  return events;
}

export function mileageEvents(entries: MileageEntry[]): ActualCostEvent[] {
  return entries.map((m) => ({
    id: m.id,
    source: "mileage",
    projectId: m.projectId,
    amountCents: m.amountCents,
    taxCents: 0,
    categoryId: MILEAGE_CATEGORY_ID,
    // Mileage is never material or labor: it must not reconcile against
    // either simply because it belongs to the same feature or category.
    costType: "other",
    bucketOverride: m.bucket,
    linkedProposedLineId: null,
    linkedFeatureId: null,
    voidedAt: m.voidedAt,
    date: m.date,
    description: m.purpose,
  }));
}

/** All events for ONE project from all sources. */
export function projectActualEvents(
  projectId: string,
  expenses: ActualExpense[],
  purchaseGraphs: PurchaseGraph[],
  mileage: MileageEntry[],
): ActualCostEvent[] {
  return [
    ...expenseEvents(expenses.filter((e) => e.projectId === projectId)),
    ...allocationEvents(purchaseGraphs).filter((e) => e.projectId === projectId),
    ...mileageEvents(mileage.filter((m) => m.projectId === projectId)),
  ];
}
